<template>

  <div :class="$style.orderAside">

    <div :class="$style.orderStepsAside"
         @click="menuClickHandler">
      <svg v-svg
           :class="$style.menuIcon"
           symbol="menu-icon"/>
      <span>Menu</span>
    </div>

    <div :class="$style.orderAsideBody">
      <h4 :class="$style.orderAsideTitle">{{ currentProduct.name }}</h4>
      <div :class="$style.orderAsideList">
        <ConfiguratorSelectedOptionsGroup
          v-for="(categoryKey, index) in sortedCategories"
          :key="index+'SelectedOption'"
          :category-key="categoryKey"
          :configurator-category="configuratorCategory"
        />
      </div>
    </div>

    <div :class="$style.orderAsideFooter">
      <div :class="$style.orderAsidePrice">
        <h6>Item Price</h6>
        <p>{{ price | toPrice }}</p>
      </div>
      <button :class="$style.orderAsideBtn"
              @click="handleStartOver">
        Start Over
      </button>
    </div>

  </div>
</template>

<script>
import Component from 'vue-class-component';
import Vue from 'vue';
import { Getter, Action } from 'vuex-class';
import { MODAL_UPDATE } from '@/apps/core/store/types';
import { CART_DISPLAY } from '@/apps/cart/store/types';
import ConfiguratorSelectedOptionsGroup from './ConfiguratorSelectedOptionsGroup';
import { CONFIGURATOR_CLEAR } from '../store/types';

/**
 * Displays current order item configuration.
 */
@Component({
  name:       'ConfiguratorCurrentBurger',
  components: {
    ConfiguratorSelectedOptionsGroup,
  },
  props: {
    configuratorCategory: {},
    sortedCategories:     {
      type:     Array,
      required: true,
    },
  },
})
export default class ConfiguratorCurrentBurger extends Vue {
  /**
   * Retrieves products from store by key.
   */
  @Getter productByKey;
  @Getter orderItemId;
  @Getter orderItemPrice;
  @Getter orderItemCompleted;
  @Getter isKioskPlatform;

  /**
   * Retrieves product key for selected product in menu.
   */
  @Getter currentProductKey;


  @Action(MODAL_UPDATE) modalUpdate;
  @Action(CONFIGURATOR_CLEAR) clearConfigurator;
  @Action(CART_DISPLAY) cartDisplay;


  /**
   * Retrieves product data from store.
   * @returns {{
   *   name: string,
   *   showInMenu: boolean,
   *   orderIndex: number,
   *   categories: [any],
   * }}
   */
  get currentProduct() {
    return this.productByKey(this.currentProductKey);
  }

  menuClickHandler() {
    if (this.orderItemCompleted) {
      this.clearConfigurator();
      this.$router.push({ name: 'menu' });

      return;
    }

    this.modalUpdate({
      name: 'goToMenuOrMain',
      data: true,
    });
  }

  /**
   * Calculate price for order item in configurator
   */
  get price() {
    return this.orderItemPrice(this.orderItemId);
  }

  /**
   * Handle "Start Over" event
   */
  handleStartOver() {
    if (this.isKioskPlatform) {
      this.cartDisplay(false);
    }

    this.modalUpdate({
      name: 'cartOrderCancel',
      data: true,
    });
  }
}
</script>

<style lang="scss" module>
  @import "~@/style/variables.scss";
  .menuIcon {
    width: 24px;
    height: 22px;
  }
  .orderStepsAside {
    height: $stepsMenuHeight;
    border-left: 1px solid $light-choice-bg;
    background: $white;
    padding-left: 20px;
    display: flex;
    align-items: center;
    cursor: pointer;
    span {
      margin-left: 16px;
      font-family: 'Gotham', sans-serif;
      font-size: 15px;
      font-weight: 700;
    }
  }
  .orderAside {
    background-color: $light-choice-bg;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    width: 100%;
  }

  .orderAsideTitle {
    font-family: 'Dekers', sans-serif;
    font-size: 21px;
    font-weight: 700;
    letter-spacing: -0.12px;
    line-height: 25px;
    padding: 35px 24px;
    color: $black;
    text-transform: capitalize;
  }

  .orderAsideBody {
    flex: 1 1 auto;
  }

  .orderAsidePrice {
    padding: 13px 24px;
    h6 {
      margin-bottom: 6px;
    }
    p {
      font-size: 20px;
      line-height: 24px;
      font-weight: 700;
    }
  }

  .orderAsideBtn {
    width: 100%;
    padding: 24px;
    font-family: 'Gotham', sans-serif;
    font-weight: 300;
    font-size: 14px;
    text-align: center;
    color: $links-color;
    line-height: 17px;
    border-top: 1px solid $black-transparent;
    outline: none;
  }

  @media screen and (max-width: $kiosk-breakpoint-max) {
    .orderStepsAside {
      display: none;
    }
    .orderAsideTitle {
      font-family: 'Gotham', sans-serif;
      font-size: 36px;
      letter-spacing: -0.4px;
      line-height: 40px;
      color: $title-black;
      margin-top: 26px;
      padding: 34px;
    }
    .orderAsidePrice {
      padding-left: 34px;
    }
    .orderAsidePrice h6 {
      font-size: 22px;
      line-height: 33px;
      letter-spacing: 0.12px;
      text-transform: uppercase;
    }
    .orderAsidePrice p {
      font-size: 34px;
      font-weight: 300;
      letter-spacing: -0.82px;
      line-height: 41px;
    }
    .orderAsideBtn {
      padding: 34px;
      border-top: none;
      text-align: left;
      font-size: 26px;
      letter-spacing: -0.37px;
      line-height: 31px;
      text-transform: lowercase;
    }
    .orderAsideFooter {
      border-top: 4px solid $white;
    }
  }

  @media screen and (max-width: 1020px) {
    .orderAsideTitle {
      font-family: 'Dekers', sans-serif;
      font-size: 21px;
      letter-spacing: -0.12px;
      line-height: 25px;
      padding: 35px 24px;
      margin-top: 0;
      color: $black;
    }
    .orderAsidePrice {
      padding-left: 24px;
    }
    .orderAsidePrice h6 {
      font-size: 15px;
      letter-spacing: -0.21px;
      line-height: 20px;
      margin-bottom: 0;
      text-transform: none;
    }
    .orderAsidePrice p {
      font-size: 20px;
      font-weight: 700;
      line-height: 24px;
    }
    .orderAsideBtn {
      width: 100%;
      padding: 24px;
      font-weight: 300;
      font-size: 14px;
      text-align: center;
      line-height: 17px;
      border-top: 1px solid $black-transparent;
      text-transform: none;
    }
  }

</style>



// WEBPACK FOOTER //
// src/apps/configurator/components/ConfiguratorCurrentBurger.vue